/**
 * Drag-a-guest-onto-a-chair.
 *
 * Implemented with pointer events and `elementFromPoint` rather than HTML5
 * drag-and-drop: the canvas lives inside a scaled/translated layer, and the
 * native API reports coordinates that ignore that transform, which makes drops
 * land on the wrong chair at any zoom other than 100%. Hit-testing the real
 * rendered pixels is transform-proof by construction.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { useEffect } from 'react'

import { avatarTint, initialsOf } from '../lib/colors'
import type { Plan } from '../lib/types'
import { usePlanStore } from '../store/planStore'
import { useViewStore } from '../store/viewStore'

interface Props {
  plan: Plan
}

const GHOST_OFFSET = 18

export function GuestDragLayer({ plan }: Props) {
  const drag = useViewStore((s) => s.drag)

  useEffect(() => {
    if (!drag) return undefined

    const seatUnder = (x: number, y: number) => {
      // The ghost sits under the cursor, so hide it for the hit test.
      const ghost = document.getElementById('guest-drag-ghost')
      const previous = ghost?.style.display
      if (ghost) ghost.style.display = 'none'
      const element = document.elementFromPoint(x, y) as HTMLElement | null
      if (ghost) ghost.style.display = previous ?? ''

      const seatEl = element?.closest('[data-seat-id]') as HTMLElement | null
      if (!seatEl) return null
      const seatId = seatEl.dataset.seatId
      const tableId = seatEl.dataset.tableId
      return seatId && tableId ? { seatId, tableId } : null
    }

    const onMove = (event: PointerEvent) => {
      event.preventDefault()
      const view = useViewStore.getState()
      view.moveDrag({ x: event.clientX, y: event.clientY })
      const target = seatUnder(event.clientX, event.clientY)
      const current = view.hoverSeat
      if (target?.seatId !== current?.seatId) view.setHoverSeat(target)
    }

    const onUp = (event: PointerEvent) => {
      const view = useViewStore.getState()
      const store = usePlanStore.getState()
      const target = seatUnder(event.clientX, event.clientY)

      if (target) {
        // Dropping onto a taken chair swaps the two guests, which is what
        // "these two should switch places" intuitively means — the same rule a
        // tap-to-seat follows, so it lives in the store (`seatGuest`).
        store.seatGuest(drag.guestId, target)
        store.setActiveSeat(target)
      } else if (drag.from) {
        // Released over empty canvas: unseat, returning the guest to the roster.
        store.clearSeat(drag.from)
      }
      view.endDrag()
    }

    const onCancel = () => useViewStore.getState().endDrag()

    window.addEventListener('pointermove', onMove, { passive: false })
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onCancel)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onCancel)
    }
  }, [drag])

  const guest = drag ? plan.guests.find((g) => g.id === drag.guestId) : null

  return (
    <AnimatePresence>
      {drag && guest && (
        <motion.div
          id="guest-drag-ghost"
          className="drag-ghost"
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.7 }}
          transition={{ type: 'spring', stiffness: 500, damping: 32 }}
          style={{ left: drag.pointer.x + GHOST_OFFSET, top: drag.pointer.y + GHOST_OFFSET }}
        >
          <span className="drag-ghost-avatar" style={{ background: avatarTint(guest.id) }}>
            {guest.photoUrl ? (
              <img src={guest.photoUrl} alt="" draggable={false} />
            ) : (
              initialsOf(guest.fullName)
            )}
          </span>
          <span className="drag-ghost-name">{guest.fullName}</span>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
